import * as wjcCore from '@mescius/wijmo';
import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {canWith, CAPS} from '../services/roles';
import {TranslocoService} from '@jsverse/transloco';

/**
 * kobe-ac プロフィール (旧 /pages/profile)。
 * 表示名と既定オークションを OUser.profile に保存 (rest/user/profile)、
 * パスワード変更タブも持つ。
 */
@Component({
    standalone: false,
    selector: 'kobeac-profile',
    templateUrl: 'profile.component.html',
})
export class KobeacProfileComponent implements OnInit {
    user: any = {};
    profile: any = {name: '', auction: null};
    auctions: any[] = [];
    password = '';
    confirm = '';

    constructor(private dataService: DataService, private http: HttpClient,
                private kobeac: KobeacService, private toasterService: ToasterService, private transloco: TranslocoService) {
    }

    async ngOnInit() {
        await this.kobeac.ensureLoaded();
        this.auctions = this.kobeac.auctions;
        this.dataService.user({}, ActivePromotionConfig.SERVER_URL).subscribe((user: any) => {
            this.user = user || {};
            this.profile = Object.assign({name: '', auction: null}, user && user.profile ? user.profile : {});
        });
    }

    /** 神戸オークション管理者か (システム管理者含む)。既定オークションは管理者用設定なので
     *  一般ユーザ (神戸オークションユーザのみ) には出さない。 */
    get isKobeAdmin(): boolean {
        return canWith(wjcCore.isObject(this.user) ? this.user.roles : null, CAPS.KOBE_ADMIN);
    }

    auctionDateFormatter = (index: number, content: any): string => {
        return this.kobeac.formatOrientDate(content);
    };

    /**
     * select の選択判定。既定オークションはオブジェクトを値に持つが、保存後に読み込んだ
     * profile.auction は auctions 配列とは別インスタンス(参照不一致)になり選択が外れる
     * (「保存すると空になる」不具合の原因)。@rid で比較して選択を維持する。
     * 文字列 rid で来る場合にも対応。
     */
    compareAuction = (a: any, b: any): boolean => {
        const rid = (x: any) => (x == null ? null : (wjcCore.isObject(x) ? x['@rid'] : x));
        const ra = rid(a);
        const rb = rid(b);
        if (ra != null && rb != null) {
            return String(ra) === String(rb);
        }
        return a === b;
    };

    /** email は空 (任意) か、メールアドレスの形になっていれば OK。 */
    get emailValid(): boolean {
        const e = (this.user && this.user.email ? String(this.user.email) : '').trim();
        return !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    }

    canSaveProfile(): boolean {
        return wjcCore.isString(this.profile.name) && this.profile.name.length > 0 && this.emailValid;
    }

    /**
     * 表示名/既定オークションを profile (embedded) に、メールアドレスを OUser トップレベル
     * (email、ユーザ管理と同じ場所) に保存する。email は SQL 埋め込みを避け部分更新で書く。
     */
    saveProfile(): void {
        if (!this.emailValid) {
            this.toasterService.pop('warning', this.transloco.translate('kobeac.profile'), this.transloco.translate('kobeac.invalid_email'));
            return;
        }
        const rid = this.user && this.user['@rid'];
        const email = (this.user && this.user.email ? String(this.user.email) : '').trim();
        const saveProfileDoc = () => {
            this.http.post(ActivePromotionConfig.SERVER_URL + '/rest/user/profile', this.profile).subscribe({
                next: (user: any) => {
                    // profile レスポンスで user を同期しつつ、直前に保存した email は維持する。
                    this.user = Object.assign({}, user, {email: email});
                    // 保存レスポンスで profile を同期し、compareAuction で選択を維持する。
                    this.profile = Object.assign({name: '', auction: null}, user && user.profile ? user.profile : this.profile);
                    this.toasterService.pop('info', this.transloco.translate('kobeac.profile'), this.transloco.translate('common.saved'));
                },
                error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.profile'), this.transloco.translate('common.failed')),
            });
        };
        if (rid) {
            this.dataService.update([{'@class': 'OUser', '@rid': rid, email: email}], ActivePromotionConfig.SERVER_URL).subscribe({
                next: () => saveProfileDoc(),
                error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.profile'), this.transloco.translate('kobeac.email_save_failed')),
            });
        } else {
            saveProfileDoc();
        }
    }

    canSavePassword(): boolean {
        return this.password.length >= 6 && this.password == this.confirm;
    }

    savePassword(): void {
        if (!wjcCore.isObject(this.user) || !this.user['@rid']) return;
        const rid = String(this.user['@rid']).replace('#', '');
        const sql = 'UPDATE ' + rid + " SET password = '" + this.password + "'";
        this.dataService.command({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.toasterService.pop('info', this.transloco.translate('kobeac.password'), this.transloco.translate('kobeac.updated'));
                this.password = '';
                this.confirm = '';
            },
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.password'), this.transloco.translate('common.failed')),
        });
    }
}
